import { type ChatMessage, isMessagePlaceholder, isMessageVideoPayload } from 'models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type MenuItemType, Text } from 'ui';
import { openInNewWindow } from 'common/utils';
import { useGetMediaSource } from 'features/media';
import { useSendChatMessage } from '../../../../hooks';
import type { MessageDirection } from '../../../../types';

type P = {
  direction: MessageDirection;
  message: ChatMessage;
};

export const useChatMessageActions = ({ direction, message }: P) => {
  const { t } = useTranslation();
  const { retryChatMessage } = useSendChatMessage();
  const getMediaSource = useGetMediaSource();

  const menuItems = useMemo((): MenuItemType[] => {
    const items: MenuItemType[] = [];
    const deliveryStatus = isMessagePlaceholder(message) ? message.status : undefined;
    const shouldShowRetry = direction === 'outgoing' && deliveryStatus === 'failed' && isMessagePlaceholder(message);

    if (shouldShowRetry) {
      items.push({
        icon: <Icon icon="refresh-1-line" />,
        key: 'retry',
        label: t('Retry'),
        onClick({ domEvent }) {
          domEvent.stopPropagation();
          retryChatMessage(message);
        },
      });
    }

    const videos = message.payload.filter((payload) => isMessageVideoPayload(payload));

    for (const video of videos) {
      const fileName = video.meta.name ?? 'video';
      const saveLabel = t('Save {{fileName}}', { fileName });

      items.push({
        icon: <Icon icon="download-2-line" />,
        key: `save-${video.ref}`,
        label: <Text ellipsis>{saveLabel}</Text>,
        onClick({ domEvent }) {
          domEvent.stopPropagation();
          openInNewWindow(getMediaSource({ id: video.ref }));
        },
      });
    }

    return items;
  }, [direction, getMediaSource, message, retryChatMessage, t]);

  return useMemo(() => ({ menuItems }), [menuItems]);
};
